import { ImgSrc } from "@config/image";
import theme from "@config/theme";
import { useState } from "react";

interface CardLayoutProps {
  cards: {
    link: string;
    title?: string;
    description: string;
    banner: string;
    date?: string;
    descriptionExtra?: React.ReactNode;
    headerExtra?: React.ReactNode;
    footerExtra?: React.ReactNode;
  }[];
  limit?: number;
}

const CardLayout = ({ cards, limit }: CardLayoutProps) => {
  const [isViewingAll, setViewingAll] = useState(limit === 0);

  return (
    <>
      <div className="cards">
        {(isViewingAll ? cards : cards.slice(0, limit)).map((card) => (
          <div className="card" key={card.date}>
            <a
              href={card.link}
              className="header"
              style={{
                backgroundImage: card.banner.startsWith("http")
                  ? `url(${card.banner})`
                  : `url(${ImgSrc(card.banner)})`,
              }}
            >
              <div className="overlay">
                <span>{card.date}</span>
                <h3>{card.title}</h3>
              </div>
            </a>
            <div className="footer">
              {card.headerExtra && <div>{card.headerExtra}</div>}
              <div>
                <p>{card.description}</p>
                {card.descriptionExtra && <div>{card.descriptionExtra}</div>}
              </div>
              {card.footerExtra && <div>{card.footerExtra}</div>}
            </div>
          </div>
        ))}
      </div>

      {limit !== undefined && limit > 0 && cards.length > limit && (
        <button
          type="button"
          className="btn btnSecondary"
          onClick={() => setViewingAll(!isViewingAll)}
        >
          <span className="button-contents">
            <span>{isViewingAll ? "See less" : "See more"}</span>
            <span className="material-icons-round">
              {isViewingAll ? "arrow_upward" : "arrow_downward"}
            </span>
          </span>
        </button>
      )}

      <style jsx>
        {`
          .cards {
            display: grid;
            grid-template-columns: repeat(
              auto-fit,
              minmax(min(100%, 20rem), ${cards.length > 2 ? "1fr" : "25rem"})
            );
            grid-gap: 2rem;
          }

          .card {
            display: flex;
            flex-direction: column;
            border: 1px solid rgba(0, 0, 0, 0.125);
            border-radius: 0.25rem;
            box-shadow: ${theme.misc.cardShadow};
          }
          .card a:hover h3 {
            text-decoration: underline;
          }

          .card .header {
            position: relative;
            color: ${theme.colors.white};
            padding: 1rem;
            aspect-ratio: 10 / 6;
            background-size: cover;
            background-position: center;
          }
          .card .overlay {
            position: absolute;
            background-image: linear-gradient(
              to bottom,
              rgba(0, 0, 0, 0.8),
              rgba(0, 0, 0, 0.1)
            );
            height: 100%;
            width: 100%;
            top: 0;
            left: 0;
            padding: 0.75rem 1.25rem;
          }

          .card a h3 {
            margin-top: 0;
          }
          .card .footer {
            color: ${theme.colors.black};
            padding: 0 1rem;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            flex-grow: 1;
          }

          .button-contents {
            display: flex;
            align-items: center;
            justify-content: center;
            grid-gap: 4px;
          }
        `}
      </style>
    </>
  );
};

CardLayout.defaultProps = {
  limit: 0,
};

export default CardLayout;
